/**
 * Advertising (Meta Pixel) consent gate.
 *
 * Default: advertising tracking is DENIED for visitors in regions that require
 * consent first, until they decide in the cookie banner. Everywhere else the
 * pixel runs without a banner.
 *
 * The region is read from the same-origin Cloudflare trace endpoint
 * (`/cdn-cgi/trace`, `loc=`). Anything we cannot trust — request failure,
 * non-OK response, unknown country (`XX`), Tor exit (`T1`), or a trace whose
 * `h=` host is not this page's host — is treated as "needs consent", so the
 * banner shows and the pixel stays off until the visitor accepts.
 *
 * Stored choice (localStorage, this browser only):
 *   key   "iamerica_ads_consent_v1"
 *   value { advertising: "granted" | "denied", at: <ISO time>, noticeVersion, region }
 * `at` + `noticeVersion` + `region` are the acceptance evidence: which choice,
 * when it was made, and which notice text it was made against.
 *
 * Withdrawal: "Cookie settings" reopens the same banner and the new choice
 * applies immediately to browser and server reporting — no reload needed.
 */

declare global {
  interface Window {
    /** Set by the index.html bootstrap once the pixel instance exists. */
    __iamericaPixelStarted?: boolean;
    fbq?: ((...args: any[]) => void) & {
      callMethod?: (...args: any[]) => void;
      queue?: any[];
      push?: any;
      loaded?: boolean;
      version?: string;
    };
    _fbq?: {
      queue?: any[];
      push?: any;
      loaded?: boolean;
      version?: string;
    };
  }
}

export const AD_CONSENT_KEY = "iamerica_ads_consent_v1";
export const NOTICE_VERSION = "2026-11-04";
export const META_PIXEL_ID = "1431666212498493";

/** Regions where an advertising tracker needs the visitor's permission first. */
export const REGULATED_REGIONS = [
  // EU / EEA
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE", "IS", "LI", "NO",
  // United Kingdom
  "GB", "IM",
  // Switzerland
  "CH",
  // Latin America
  "CO", "BR",
];

export type AdsChoice = "granted" | "denied";

export interface AdsConsentRecord {
  advertising: AdsChoice;
  at: string;
  noticeVersion: string;
  region: string;
}

export interface RegionInfo {
  /** Two-letter country from the trace endpoint, or "" when unresolved. */
  loc: string;
  /** True when the region could not be trusted (trace failed / XX / T1 / host mismatch). */
  unresolved: boolean;
  /** True when consent is required before the pixel may run. */
  regulated: boolean;
}

/* ------------------------------------------------------------------ */
/* Region lookup — cached for the page visit                           */
/* ------------------------------------------------------------------ */

let regionPromise: Promise<RegionInfo> | null = null;

const UNRESOLVED: RegionInfo = { loc: "", unresolved: true, regulated: true };

function traceField(text: string, name: string): string {
  const m = new RegExp("(?:^|\\n)" + name + "=([^\\n]*)").exec(text);
  return m ? m[1].trim() : "";
}

export function getRegion(): Promise<RegionInfo> {
  if (regionPromise) return regionPromise;

  regionPromise = new Promise<RegionInfo>((resolve) => {
    if (typeof window === "undefined") {
      resolve(UNRESOLVED);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 2000);

    fetch("/cdn-cgi/trace", { signal: controller.signal })
      .then((res) => {
        clearTimeout(timer);
        if (!res.ok) return resolve(UNRESOLVED);
        return res.text().then((text) => {
          const loc = traceField(text, "loc").toUpperCase();
          const host = traceField(text, "h");
          // A trace whose host is not this page's host is a rewrite proxy, not
          // a real region answer — fall back to "needs consent".
          if (host && host !== window.location.hostname) return resolve(UNRESOLVED);
          if (!loc || loc === "XX" || loc === "T1") return resolve(UNRESOLVED);
          resolve({ loc, unresolved: false, regulated: REGULATED_REGIONS.includes(loc) });
        });
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(UNRESOLVED);
      });
  });

  return regionPromise;
}

/* ------------------------------------------------------------------ */
/* Stored choice                                                       */
/* ------------------------------------------------------------------ */

export function readAdsConsent(): AdsConsentRecord | null {
  try {
    const raw = localStorage.getItem(AD_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AdsConsentRecord;
    if (parsed && (parsed.advertising === "granted" || parsed.advertising === "denied")) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function writeAdsConsent(choice: AdsChoice, region: string): AdsConsentRecord {
  const record: AdsConsentRecord = {
    advertising: choice,
    at: new Date().toISOString(),
    noticeVersion: NOTICE_VERSION,
    region,
  };
  try {
    localStorage.setItem(AD_CONSENT_KEY, JSON.stringify(record));
  } catch {
    // Storage unavailable (private mode): the choice still applies to this page.
  }
  notifyAdsConsent();
  return record;
}

export function clearAdsConsent(): void {
  try {
    localStorage.removeItem(AD_CONSENT_KEY);
  } catch {
    // ignore
  }
  notifyAdsConsent();
}

/* ------------------------------------------------------------------ */
/* Decision                                                            */
/* ------------------------------------------------------------------ */

/**
 * May the pixel run right now?
 * - explicit accept  -> yes, anywhere
 * - explicit refuse  -> no, anywhere
 * - no answer yet    -> yes only when the region resolved and is not regulated
 */
export async function adsAllowed(): Promise<boolean> {
  const stored = readAdsConsent();
  if (stored) return stored.advertising === "granted";
  const region = await getRegion();
  return !region.regulated;
}

/* ------------------------------------------------------------------ */
/* Pixel plumbing                                                      */
/* ------------------------------------------------------------------ */

let pixelStarted = false;

function fbqCall(args: any[]): void {
  const fbq = typeof window !== "undefined" ? window.fbq : undefined;
  if (typeof fbq !== "function") return;
  if (typeof fbq.callMethod === "function") {
    fbq.callMethod.apply(fbq, args);
  } else if (Array.isArray(fbq.queue)) {
    fbq.queue.push(args);
  }
}

/** Load the pixel instance and count this page view. Idempotent per page. */
export function startPixel(): void {
  if (pixelStarted) return;
  // The index.html bootstrap may already have initialised it this page view —
  // initialising twice would report the page view twice.
  if (typeof window !== "undefined" && window.__iamericaPixelStarted) {
    pixelStarted = true;
    return;
  }
  pixelStarted = true;
  if (typeof window !== "undefined") window.__iamericaPixelStarted = true;
  fbqCall(["init", META_PIXEL_ID, {}, { agent: "lovable" }]);
  fbqCall(["track", "PageView"]);
}

/** Stop the pixel for this visitor without reloading the page. */
export function stopPixel(): void {
  fbqCall(["consent", "revoke"]);
}

/** Bring the pixel in line with the visitor's current choice. */
export async function applyAdsConsent(): Promise<void> {
  const stored = readAdsConsent();
  if (stored && stored.advertising === "denied") {
    stopPixel();
    return;
  }
  if (await adsAllowed()) startPixel();
}

/* ------------------------------------------------------------------ */
/* Change notifications + "Cookie settings" reopen                     */
/* ------------------------------------------------------------------ */

const CHANGE_EVENT = "iamerica:ads-consent-change";
const OPEN_EVENT = "iamerica:open-cookie-settings";

export function notifyAdsConsent(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/** Reopen the banner so a visitor can change their mind. */
export function openCookieSettings(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

export function subscribeAdsConsent(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = () => handler();
  window.addEventListener(CHANGE_EVENT, listener);
  return () => window.removeEventListener(CHANGE_EVENT, listener);
}

export function subscribeCookieSettingsOpen(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = () => handler();
  window.addEventListener(OPEN_EVENT, listener);
  return () => window.removeEventListener(OPEN_EVENT, listener);
}
